import { useState, useEffect, useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import api from '@/services/api'
import { getFriendlyError } from '@/utils/getFriendlyError'
import { canReadParceiros } from '@/utils/permissions'
import { useAuthStore } from '@/stores/authStore'
import { EMPRESAS_PAGE_SIZE } from '@/pages/empresasPageConstants'
import { buildEmpresasListParams } from '@/hooks/buildEmpresasListParams'
import type { EmpresaDetailed, Contact, Partner } from '@/types'

export function useEmpresasPage() {
  const { user } = useAuthStore()
  const [searchEmpresa, setSearchEmpresa] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [searchContato, setSearchContato] = useState('')
  const [page, setPage] = useState(1)
  const [selectedEmpresa, setSelectedEmpresa] = useState<EmpresaDetailed | null>(null)
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list')
  const [filterNoContact, setFilterNoContact] = useState(false)
  const [filterSegmento, setFilterSegmento] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [empresas, setEmpresas] = useState<EmpresaDetailed[]>([])
  const [totalItems, setTotalItems] = useState(0)
  const [contatos, setContatos] = useState<Contact[]>([])
  const [parceiros, setParceiros] = useState<Partner[]>([])
  const [segmentos, setSegmentos] = useState<string[]>([])
  const [empresaCatalog, setEmpresaCatalog] = useState<Pick<EmpresaDetailed, 'id' | 'segmento' | 'status'>[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()

  const viewType = searchParams.get('view') === 'contatos' ? 'contatos' : 'empresas'
  const setViewType = (v: 'empresas' | 'contatos') => {
    setSearchParams(v === 'contatos' ? { view: 'contatos' } : {})
    setPage(1)
  }

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchEmpresa.trim()), 300)
    return () => window.clearTimeout(t)
  }, [searchEmpresa])

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, filterStatus])

  const clientExtraFilter = Boolean(filterNoContact || filterSegmento)

  const fetchEmpresas = useCallback(
    async (signal?: AbortSignal) => {
      const params = buildEmpresasListParams({
        page: clientExtraFilter ? 1 : page,
        // Segmento / sem contato ainda são client-side → amostra maior
        limit: clientExtraFilter ? 100 : EMPRESAS_PAGE_SIZE,
        search: debouncedSearch,
        status: filterStatus,
      })
      const empRes = await api.get('/empresas', { params, signal })
      setEmpresas(empRes.data.data ?? [])
      setTotalItems(Number(empRes.data.meta?.totalItems ?? empRes.data.data?.length ?? 0))
    },
    [page, debouncedSearch, filterStatus, clientExtraFilter],
  )

  useEffect(() => {
    const controller = new AbortController()
    const fetchData = async () => {
      setLoading(true)
      try {
        const parceirosRequest = canReadParceiros(user?.role)
          ? api.get('/parceiros', { signal: controller.signal })
          : Promise.resolve({ data: { data: [] } })

        const [, contRes, parRes] = await Promise.all([
          fetchEmpresas(controller.signal),
          api.get('/contatos', { signal: controller.signal }),
          parceirosRequest,
        ])
        setContatos(contRes.data.data ?? [])
        setParceiros(parRes.data.data ?? [])
      } catch (err: unknown) {
        if (!controller.signal.aborted) {
          setEmpresas([])
          setTotalItems(0)
          setContatos([])
          toast.error(getFriendlyError(err, 'Erro ao carregar empresas e contatos'))
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }
    void fetchData()
    return () => controller.abort()
  }, [user?.role, fetchEmpresas])

  /** Segmentos distintos (amostra recente) para o filtro. */
  useEffect(() => {
    const controller = new AbortController()
    api
      .get('/empresas', {
        params: { page: 1, limit: 100, sortBy: 'createdAt', sortOrder: 'desc' },
        signal: controller.signal,
      })
      .then((resp) => {
        const rows = (resp.data.data ?? []) as EmpresaDetailed[]
        const segs = Array.from(
          new Set(rows.map((e) => e.segmento).filter((s): s is string => Boolean(s))),
        ).sort((a, b) => a.localeCompare(b, 'pt-BR'))
        setSegmentos(segs)
        setEmpresaCatalog(rows.map((empresa) => ({
          id: empresa.id,
          segmento: empresa.segmento,
          status: empresa.status,
        })))
      })
      .catch(() => {})
    return () => controller.abort()
  }, [])

  const empresaById = useMemo(() => {
    return new Map(empresaCatalog.map((empresa) => [empresa.id, empresa]))
  }, [empresaCatalog])

  const filteredContatos = useMemo(() => {
    return contatos.filter((c) => {
      const q = searchContato.toLowerCase().trim()
      const matchesSearch = !q || (
        (c.nome || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.cargo || '').toLowerCase().includes(q) ||
        (c.telefone || '').toLowerCase().includes(q) ||
        (c.empresaNome || '').toLowerCase().includes(q)
      )
      if (!matchesSearch) return false
      if (filterNoContact && (c.telefone || c.email)) return false
      const empresa = empresaById.get(c.empresaId)
      if (filterSegmento && (empresa?.segmento ?? '') !== filterSegmento) return false
      if (filterStatus && (empresa?.status ?? '') !== filterStatus) return false
      return true
    })
  }, [contatos, searchContato, filterNoContact, filterSegmento, filterStatus, empresaById])

  /** Segmento / sem contato ainda no client (API não filtra esses). */
  const filteredEmpresas = useMemo(() => {
    return empresas.filter((e) => {
      const hasContact =
        e.contato || (e.contatosCount && e.contatosCount > 0) || e.email || e.telefone
      if (filterNoContact && hasContact) return false
      if (filterSegmento && (e.segmento ?? '') !== filterSegmento) return false
      return true
    })
  }, [empresas, filterNoContact, filterSegmento])

  const filtered = viewType === 'contatos' ? filteredContatos : filteredEmpresas
  const totalCount =
    viewType === 'contatos'
      ? filteredContatos.length
      : clientExtraFilter
        ? filteredEmpresas.length
        : totalItems
  const totalPages =
    viewType === 'contatos' || clientExtraFilter
      ? Math.max(1, Math.ceil(filtered.length / EMPRESAS_PAGE_SIZE))
      : Math.max(1, Math.ceil(totalItems / EMPRESAS_PAGE_SIZE))

  const paginatedEmpresas =
    viewType === 'empresas' && clientExtraFilter
      ? (filtered.slice((page - 1) * EMPRESAS_PAGE_SIZE, page * EMPRESAS_PAGE_SIZE) as EmpresaDetailed[])
      : (filteredEmpresas as EmpresaDetailed[])

  const paginatedContatos = filteredContatos.slice(
    (page - 1) * EMPRESAS_PAGE_SIZE,
    page * EMPRESAS_PAGE_SIZE,
  ) as Contact[]

  const handleSaved = useCallback(() => {
    void fetchEmpresas().catch(() => {})
    void api
      .get('/contatos')
      .then((resp) => setContatos(resp.data.data ?? []))
      .catch(() => {})
  }, [fetchEmpresas])

  const handleSelectContato = useCallback(
    (c: Contact) => {
      const emp = empresas.find((e) => e.id === c.empresaId)
      if (emp) setSelectedEmpresa(emp)
    },
    [empresas],
  )

  return {
    searchEmpresa,
    setSearchEmpresa,
    searchContato,
    setSearchContato,
    page,
    setPage,
    selectedEmpresa,
    setSelectedEmpresa,
    viewMode,
    setViewMode,
    filterNoContact,
    setFilterNoContact,
    filterSegmento,
    setFilterSegmento,
    filterStatus,
    setFilterStatus,
    parceiros,
    loading,
    showCreateModal,
    setShowCreateModal,
    viewType,
    setViewType,
    segmentos,
    filtered,
    totalPages,
    paginatedEmpresas,
    paginatedContatos,
    handleSaved,
    handleSelectContato,
    pageSize: EMPRESAS_PAGE_SIZE,
    totalCount,
  }
}
